import React, { useState } from 'react';
import {
  Card,
  Text,
  Button,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Collapsible,
  Box,
  Icon,
  Tooltip,
  Modal,
  TextField,
} from '@shopify/polaris';
import {
  StatusActiveIcon,
  AlertCircleIcon,
  RefreshIcon,
  ClipboardIcon,
  CircleDownIcon,
} from '@shopify/polaris-icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';

interface AgentStatusData {
  registered: boolean;
  connected: boolean;
  tallyConnected: boolean;
  status: 'online' | 'offline' | 'connecting' | 'unregistered';
  lastSeenAt?: string;
  agentVersion?: string;
  osInfo?: string;
  tallyCompany?: string;
  tallyVersion?: string;
  hasRegistrationCode?: boolean;
  registrationCode?: string;
  registrationCodeExpiresAt?: string;
}

interface RegistrationCodeData {
  code: string;
  expiresAt: string;
}

const AgentStatus: React.FC = () => {
  const queryClient = useQueryClient();
  const [showSetupInstructions, setShowSetupInstructions] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationCode, setRegistrationCode] = useState<RegistrationCodeData | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch agent status
  const { data: statusData, isLoading, error } = useQuery(
    'agentStatus',
    () => api.agent.getStatus(),
    {
      refetchInterval: 10000, // Refresh every 10 seconds
    }
  );

  // Generate registration code mutation
  const generateCodeMutation = useMutation(
    () => api.agent.generateRegistrationCode(),
    {
      onSuccess: (response) => {
        setRegistrationCode({
          code: response.data.code,
          expiresAt: response.data.expiresAt,
        });
        setShowRegistrationModal(true);
        // Refresh status so hasRegistrationCode / the stored code reflect the new one.
        queryClient.invalidateQueries('agentStatus');
      },
    }
  );

  // Revoke agent mutation
  const revokeMutation = useMutation(
    () => api.agent.revokeAgent(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agentStatus');
      },
    }
  );

  const status: AgentStatusData | undefined = statusData?.data?.status;

  const handleCopyCode = () => {
    if (registrationCode?.code) {
      navigator.clipboard.writeText(registrationCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;

    if (status.connected && status.tallyConnected) {
      return <Badge tone="success">Online & Syncing</Badge>;
    } else if (status.connected) {
      return <Badge tone="warning">Connected (Tally Offline)</Badge>;
    } else if (status.registered) {
      return <Badge tone="critical">Offline</Badge>;
    } else if (status.hasRegistrationCode) {
      return <Badge tone="attention">Pending Setup</Badge>;
    } else {
      return <Badge>Not Configured</Badge>;
    }
  };

  const formatLastSeen = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (isLoading) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Tally Sync Agent</Text>
          <Text as="p" tone="subdued">Loading agent status...</Text>
        </BlockStack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Banner tone="warning">
          Unable to fetch agent status. Please check your connection.
        </Banner>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <BlockStack gap="400">
          {/* Header */}
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h2" variant="headingMd">Tally Sync Agent</Text>
              {getStatusBadge()}
            </InlineStack>
            <Button
              icon={RefreshIcon}
              onClick={() => queryClient.invalidateQueries('agentStatus')}
              accessibilityLabel="Refresh status"
            />
          </InlineStack>

          {/* Status Banner */}
          {status?.connected && status?.tallyConnected && (
            <Banner tone="success">
              Agent is connected and syncing orders to Tally automatically.
            </Banner>
          )}

          {status?.connected && !status?.tallyConnected && (
            <Banner tone="warning">
              Agent is connected but Tally is not responding. Please ensure Tally is running.
            </Banner>
          )}

          {status?.registered && !status?.connected && (
            <Banner tone="critical">
              Agent is offline. Start the agent on your Tally machine to resume syncing.
            </Banner>
          )}

          {!status?.registered && !status?.hasRegistrationCode && (
            <Banner tone="info">
              Set up the Tally Sync Agent to automatically sync orders to your local Tally.
            </Banner>
          )}

          {status?.hasRegistrationCode && (
            <Banner tone="warning">
              A registration code has been generated. Complete the setup on your Tally machine.
            </Banner>
          )}

          {/* Status Details */}
          {(status?.registered || status?.hasRegistrationCode) && (
            <Box paddingBlockStart="200">
              <BlockStack gap="200">
                <InlineStack gap="400">
                  <Text as="span" tone="subdued">Status:</Text>
                  <Text as="span">{status?.status || 'Unknown'}</Text>
                </InlineStack>

                {status?.lastSeenAt && (
                  <InlineStack gap="400">
                    <Text as="span" tone="subdued">Last seen:</Text>
                    <Text as="span">{formatLastSeen(status.lastSeenAt)}</Text>
                  </InlineStack>
                )}

                {status?.tallyCompany && (
                  <InlineStack gap="400">
                    <Text as="span" tone="subdued">Tally Company:</Text>
                    <Text as="span">{status.tallyCompany}</Text>
                  </InlineStack>
                )}

                {status?.agentVersion && (
                  <InlineStack gap="400">
                    <Text as="span" tone="subdued">Agent Version:</Text>
                    <Text as="span">{status.agentVersion}</Text>
                  </InlineStack>
                )}

                {status?.osInfo && (
                  <InlineStack gap="400">
                    <Text as="span" tone="subdued">System:</Text>
                    <Text as="span">{status.osInfo}</Text>
                  </InlineStack>
                )}
              </BlockStack>
            </Box>
          )}

          {/* Actions */}
          <InlineStack gap="200">
            {!status?.registered && !status?.hasRegistrationCode && (
              <Button
                variant="primary"
                onClick={() => generateCodeMutation.mutate()}
                loading={generateCodeMutation.isLoading}
              >
                Set Up Agent
              </Button>
            )}

            {status?.hasRegistrationCode && (
              <Button
                onClick={() => {
                  if (statusData?.data?.status?.registrationCode) {
                    setRegistrationCode({
                      code: statusData.data.status.registrationCode,
                      expiresAt: status.registrationCodeExpiresAt || '',
                    });
                  }
                  setShowRegistrationModal(true);
                }}
              >
                View Registration Code
              </Button>
            )}

            {status?.hasRegistrationCode && (
              <Button
                onClick={() => generateCodeMutation.mutate()}
                loading={generateCodeMutation.isLoading}
              >
                Generate New Code
              </Button>
            )}

            {status?.registered && (
              <Button
                tone="critical"
                onClick={() => revokeMutation.mutate()}
                loading={revokeMutation.isLoading}
              >
                Disconnect Agent
              </Button>
            )}

            <Button
              variant="plain"
              onClick={() => setShowSetupInstructions(!showSetupInstructions)}
            >
              {showSetupInstructions ? 'Hide' : 'Show'} Setup Instructions
            </Button>
          </InlineStack>

          {/* Setup Instructions */}
          <Collapsible
            open={showSetupInstructions}
            id="setup-instructions"
            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
          >
            <Box paddingBlockStart="400" paddingBlockEnd="200">
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">Setup Instructions</Text>

                <BlockStack gap="200">
                  <Text as="p">
                    <strong>Step 1:</strong> Download the Tally Sync Agent for your operating system:
                  </Text>
                  <InlineStack gap="200">
                    <Button icon={CircleDownIcon} url="/downloads/tally-sync-win.exe">
                      Windows
                    </Button>
                    <Button icon={CircleDownIcon} url="/downloads/tally-sync-macos" disabled>
                      macOS (Coming Soon)
                    </Button>
                  </InlineStack>
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="p">
                    <strong>Step 2:</strong> Open a terminal/command prompt and run:
                  </Text>
                  <Box background="bg-surface-secondary" padding="200" borderRadius="100">
                    <Text as="p" fontWeight="medium">
                      <code>tally-sync configure</code>
                    </Text>
                  </Box>
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="p">
                    <strong>Step 3:</strong> Enter the registration code when prompted.
                  </Text>
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="p">
                    <strong>Step 4:</strong> Start the agent:
                  </Text>
                  <Box background="bg-surface-secondary" padding="200" borderRadius="100">
                    <Text as="p" fontWeight="medium">
                      <code>tally-sync start</code>
                    </Text>
                  </Box>
                </BlockStack>

                <Text as="p" tone="subdued">
                  The agent will connect to the cloud server and start syncing orders automatically.
                  Make sure Tally Prime is running with HTTP Server enabled on port 9000.
                </Text>
              </BlockStack>
            </Box>
          </Collapsible>
        </BlockStack>
      </Card>

      {/* Registration Code Modal */}
      <Modal
        open={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        title="Registration Code"
        primaryAction={{
          content: copied ? 'Copied!' : 'Copy Code',
          onAction: handleCopyCode,
          icon: ClipboardIcon,
        }}
        secondaryActions={[
          {
            content: 'Generate New Code',
            onAction: () => generateCodeMutation.mutate(),
            loading: generateCodeMutation.isLoading,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p">
              Enter this code in the Tally Sync Agent to complete registration:
            </Text>

            <Box
              background="bg-surface-secondary"
              padding="400"
              borderRadius="200"
            >
              <Text as="p" variant="headingLg" alignment="center" fontWeight="bold">
                {registrationCode?.code || '----'}
              </Text>
            </Box>

            {registrationCode?.expiresAt && (
              <Text as="p" tone="subdued" alignment="center">
                This code expires on{' '}
                {new Date(registrationCode.expiresAt).toLocaleString()}
              </Text>
            )}

            <Banner tone="info">
              Run <code>tally-sync configure</code> on your Tally machine and enter this code when prompted.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default AgentStatus;
